import { supabase } from './supabaseClient';
import { normalizePhilippineMobile } from './phoneNumber';

const STATUS_ALIASES = {
  new: 'pending_review',
  in_review: 'pending_review',
  pending_review: 'pending_review',
  approved: 'accepted',
  accepted: 'accepted',
  initial_consultation: 'initial_consultation',
  proposal_quotation: 'proposal_contract',
  agreement_confirmation: 'proposal_contract',
  contract_signing: 'proposal_contract',
  proposal_contract: 'proposal_contract',
  event_preparation: 'event_preparation',
  event_live: 'event_live',
  declined: 'declined',
};

export const canonicalizeBookingStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase().replace(/\s+/g, '_');
  return STATUS_ALIASES[normalized] || normalized || 'pending_review';
};

export const resolveAuthenticatedProfile = ({ user, cachedEmail = '', cachedName = '', phone = '' } = {}) => {
  const email = String(user?.email || cachedEmail || '').trim();
  const metadataName = String(user?.user_metadata?.full_name || user?.user_metadata?.name || '').trim();
  const name = metadataName
    || String(cachedName || '').trim()
    || (email ? email.split('@')[0].replace(/[._-]+/g, ' ').trim() : '');

  return { email, name, phone: String(phone || '').trim() };
};

export const persistLocalBookingProfile = ({ email = '', name = '' } = {}) => {
  const nextEmail = String(email || '').trim();
  const nextName = String(name || '').trim();

  if (nextEmail) localStorage.setItem('userEmail', nextEmail);
  if (nextName) localStorage.setItem('userName', nextName);

  return { email: nextEmail, name: nextName };
};

export const buildPublicUserSyncRow = ({ id, email, full_name, phone, existing = null } = {}) => {
  const row = {
    id,
    email: String(email || '').trim(),
    full_name: String(full_name || '').trim(),
  };

  if (phone !== undefined) {
    row.phone = phone ? String(phone).trim() : null;
  }

  if (!existing) {
    row.role = 'customer';
    row.status = 'Active';
  }

  return row;
};

const isMissingPhoneColumnError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('phone') && (message.includes('column') || message.includes('schema cache'));
};

export const isSameEmail = (left, right) => (
  String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase()
);

export const isValidEmailOtp = (value) => /^\d{6}$/.test(String(value || '').trim());

export const syncPublicUserProfile = async ({ id, email, full_name, phone }) => {
  if (!id) {
    throw new Error('Missing user id. Please sign in again.');
  }

  const { data: existing, error: readError } = await supabase
    .from('users')
    .select('id')
    .eq('id', id)
    .maybeSingle();

  if (readError && readError.code !== 'PGRST116') {
    throw new Error(readError.message || 'Unable to read profile.');
  }

  const row = buildPublicUserSyncRow({
    id,
    email,
    full_name,
    phone,
    existing: existing?.id ? existing : null,
  });

  let { error } = await supabase.from('users').upsert(row, { onConflict: 'id' });
  if (error && phone !== undefined && isMissingPhoneColumnError(error)) {
    const { phone: _phone, ...withoutPhone } = row;
    ({ error } = await supabase.from('users').upsert(withoutPhone, { onConflict: 'id' }));
    if (!error) {
      throw new Error('Phone number could not be saved. Run the users phone column SQL in Supabase, then try again.');
    }
  }
  if (error) {
    throw new Error(error.message || 'Unable to sync profile to the database.');
  }
};

export const loadAuthenticatedBookingProfile = async () => {
  const cachedEmail = localStorage.getItem('userEmail') || '';
  const cachedName = localStorage.getItem('userName') || '';

  const {
    data: { user },
  } = await supabase.auth.getUser();

  let phone = '';
  if (user?.id) {
    const { data: userRow, error: phoneError } = await supabase
      .from('users')
      .select('phone')
      .eq('id', user.id)
      .maybeSingle();

    if (!phoneError) {
      phone = String(userRow?.phone || '').trim();
    }
  }

  const profile = resolveAuthenticatedProfile({ user, cachedEmail, cachedName, phone });
  persistLocalBookingProfile(profile);
  return profile;
};

export const getCurrentBookingEmail = async () => {
  const profile = await loadAuthenticatedBookingProfile();
  return profile.email;
};

export const getCurrentBookingName = async () => {
  const profile = await loadAuthenticatedBookingProfile();
  return profile.name;
};

const generateRequestCode = () => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `EJH-${timestamp}-${suffix}`;
};

const normalizeNumberOrNull = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
  return Math.trunc(numericValue);
};

const normalizeString = (value) => String(value || '').trim();
const LEGACY_UUID_PLACEHOLDER = '00000000-0000-0000-0000-000000000000';

const isLegacyNotNullError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return (
    (error?.code === '23502' || message.includes('violates not-null constraint'))
    && (message.includes('artist_id') || message.includes('event_id'))
  );
};

const isUuidTypeError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('uuid');
};

const isForeignKeyError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return error?.code === '23503' || message.includes('foreign key');
};

const insertBooking = async (row) => {
  const { data, error } = await supabase
    .from('bookings')
    .insert(row)
    .select('*')
    .single();

  return { data, error };
};

export const createAdminBookingNotification = async (bookingRow, overrides = {}) => {
  if (!bookingRow?.id) return;

  const bookingType = String(bookingRow.booking_type || 'booking').trim();
  const requesterName = String(bookingRow.requester_name || 'Client').trim() || 'Client';
  const eventName = String(bookingRow.event_name || bookingRow.request_code || 'Booking request').trim();

  const notificationPayload = {
    title: String(overrides.title || `New booking request: ${eventName}`),
    message: String(overrides.message || `${requesterName} submitted a ${bookingType} booking request.`),
    meta: String(overrides.meta || 'Bookings'),
    scope: String(overrides.scope || 'admin'),
    is_read: false,
  };

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user?.id) {
    notificationPayload.created_by = user.id;
  }

  const { error } = await supabase.from('notifications').insert(notificationPayload);
  if (error) {
    // Keep booking creation successful even if notification insert is blocked by RLS.
    console.warn('Booking created but admin notification insert failed:', error.message || error);
  }
};

export const fetchCurrentUserBookings = async () => {
  const email = await getCurrentBookingEmail();
  if (!email) {
    return { email: '', bookings: [] };
  }

  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('requester_email', email)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return { email, bookings: data || [] };
};

export const createBookingRequest = async (payload) => {
  const { email: requesterEmail, name: requesterName, phone: profilePhone } = await loadAuthenticatedBookingProfile();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!requesterEmail) {
    throw new Error('Missing requester email. Please sign in again.');
  }

  const bookingRow = {
    request_code: generateRequestCode(),
    requester_name: normalizeString(requesterName),
    requester_email: normalizeString(requesterEmail),
    booking_type: normalizeString(payload.booking_type),
    status: 'new',
    event_name: normalizeString(payload.event_name),
    budget_range: normalizeString(payload.budget_range),
    theme: normalizeString(payload.theme),
    venue: normalizeString(payload.venue),
    event_date: payload.event_date ? payload.event_date : null,
    attendee_count: normalizeNumberOrNull(payload.attendee_count),
    event_type: normalizeString(payload.event_type),
    details: normalizeString(payload.details),
    specific_needs: normalizeString(payload.specific_needs),
    concert_vendor_choices: payload.concert_vendor_choices || {},
    attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
  };

  const requesterPhone = normalizePhilippineMobile(payload.requester_phone || profilePhone);
  const companyName = normalizeString(payload.company_name);
  if (requesterPhone) bookingRow.requester_phone = requesterPhone;
  if (companyName) bookingRow.company_name = companyName;

  let { data, error } = await insertBooking(bookingRow);

  if (error && isLegacyNotNullError(error)) {
    const integerFallbackRow = {
      ...bookingRow,
      artist_id: Number.isFinite(Number(payload.artist_id)) ? Number(payload.artist_id) : 0,
      event_id: Number.isFinite(Number(payload.event_id)) ? Number(payload.event_id) : 0,
    };

    ({ data, error } = await insertBooking(integerFallbackRow));

    if (error && isUuidTypeError(error)) {
      const uuidFallbackRow = {
        ...bookingRow,
        artist_id: normalizeString(payload.artist_id) || user?.id || LEGACY_UUID_PLACEHOLDER,
        event_id: normalizeString(payload.event_id) || LEGACY_UUID_PLACEHOLDER,
      };

      ({ data, error } = await insertBooking(uuidFallbackRow));
    }

    if (error && isForeignKeyError(error)) {
      throw new Error('Booking table still requires linked artist/event records. Please update the Supabase bookings schema to allow request-based submissions.');
    }
  }

  if (error) throw error;

  await createAdminBookingNotification(data);

  return data;
};